import type { CseLabels } from "@/modules/companies/client/api/types"

import dynamic from "next/dynamic"
import { type FunctionComponent, useState } from "react"

import { Heading } from "@jobteaser/spark/components/Heading"
import { Text } from "@jobteaser/spark/components/Text"
import IconInformationOutline from "@jobteaser/ui-kit/assets/icons/jds_informationOutline.svg"
import IconStarFull from "@jobteaser/ui-kit/assets/icons/jds_starFull.svg"
import { Button } from "@jobteaser/ui-kit/components/Button"
import { Icon, ICON_SIZES } from "@jobteaser/ui-kit/dist/module/components/Icon/Icon"
import { Layout, LAYOUT_COLOR_BLOCK } from "@jobteaser/ui-kit/dist/module/components/Layout/Layout"
import { Tooltip } from "@jobteaser/ui-kit/dist/module/components/Tooltip/Tooltip"

import { useCompanyInsightsFetch } from "@/modules/community/hooks/useCompanyInsightsFetch"
import { useCompanyFetch } from "@/modules/companies/client/hooks/useCompanyFetch"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useTracking } from "@/modules/tracking/useTracking"

import styles from "./Commitments.module.css"

const CommitmentsModalDynamic = dynamic(() =>
  import("@/modules/companies/client/components/Commitments/CommitmentsModal").then(mod => mod.CommitmentsModal)
)

export const Commitments: FunctionComponent = () => {
  const tracking = useTracking()
  const { t } = useTranslation(["companies_client"])
  const { dataCompany, isSuccessCompany } = useCompanyFetch()
  const { dataCompanyInsights } = useCompanyInsightsFetch(dataCompany?.id || "")
  const [isModalDisplayed, setIsModalDisplayed] = useState<boolean>(false)

  if (!dataCompany || !isSuccessCompany) return null

  const {
    id,
    page: { name, cse },
  } = dataCompany
  const socialEnvCommitmentCount = dataCompanyInsights?.experiencesInfos.socialEnvCommitmentCount || 0
  const socialEnvCommitmentScore = dataCompanyInsights?.experiencesInfos.socialEnvCommitmentScore || 0

  const displayMore = (): void => {
    setIsModalDisplayed(prev => !prev)
    tracking?.track("see_more_information_click", {
      company_id: id,
      company_name: name,
      context: "commitments",
      service: "company",
    })
  }

  const labelUrlIsNotEmpty = (labels: CseLabels): boolean => {
    for (const [_, { url }] of Object.entries(labels)) {
      if (url.trim() !== "") return true
    }
    return false
  }

  const displayCommitments = cse.actions.length > 0 || cse.commitments || labelUrlIsNotEmpty(cse.labels)

  return displayCommitments ? (
    <Layout hasTopPadding colorBlock={LAYOUT_COLOR_BLOCK.WHITE} data-testid="companies_information_commitments">
      <Layout.Column>
        <Heading
          className={styles.title}
          data-testid="company_information_commitments_title"
          as="h2"
          variant="title2"
          weight="bold"
        >
          {t("companies_client.information.commitments.title", { name })}
        </Heading>
        <div className={styles.commitments}>
          <div className={styles.commitments__head}>
            <Heading as="h3" variant="title3" data-testid="companies_information_commitments_subtitle">
              {cse.actions.length > 0
                ? t("companies_client.information.commitments.actionsAccomplished", { count: cse.actions.length })
                : t("companies_client.information.commitments.csrCommitments")}
            </Heading>
            <Button minor onClick={displayMore}>
              {t("companies_client.information.commitments.findOutMore")}
            </Button>
          </div>
          {cse.actions.length > 0 && (
            <ul className={styles.commitments__actions} data-testid="companies_information_commitments_actions_list">
              {cse.actions.map(action => (
                <li key={action}>
                  <Text variant="body1">{action}</Text>
                </li>
              ))}
            </ul>
          )}
          {socialEnvCommitmentCount > 0 && (
            <div
              className={styles.commitments__studentsReviews}
              data-testid="companies_information_commitments_student_reviews"
            >
              <Text variant="body1" weight="semiBold">
                {t("companies_client.information.commitments.studentsReviews", { name })}
              </Text>
              <Tooltip position="bottom" label={t("companies_client.information.commitments.scoreTooltip")}>
                <Icon xlinkHref={`#${IconInformationOutline.id}`} role="graphics-symbol" />
              </Tooltip>
              <div className={styles.commitments__averageScore}>
                <Icon xlinkHref={`#${IconStarFull.id}`} size={ICON_SIZES.BIG} role="graphics-symbol" />
                <Text variant="body1" weight="semiBold" data-testid="companies_information_commitments_average_rate">
                  {`${socialEnvCommitmentScore}/5`}
                </Text>
                <Text variant="body2" data-testid="companies_information_commitments_total_rate">
                  {t("companies_client.information.commitments.totalRate", {
                    count: socialEnvCommitmentCount,
                  })}
                </Text>
              </div>
            </div>
          )}
        </div>
        {isModalDisplayed && <CommitmentsModalDynamic cse={cse} onClose={() => setIsModalDisplayed(false)} />}
      </Layout.Column>
    </Layout>
  ) : null
}
